import React from 'react';
import { getPassengers, setPassengerActive } from '../../services/admin';

const PAGE_SIZE = 50;

type PassengerRecord = {
  id: string;
  initials: string;
  name: string;
  email: string;
  phone: string;
  status: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  trips: number;
  joined: string;
};

export function Passengers({ searchQuery = '' }: { searchQuery?: string }) {
  const [passengers, setPassengers] = React.useState<PassengerRecord[]>([]);
  const [selectedPassenger, setSelectedPassenger] = React.useState<PassengerRecord | null>(null);
  const [deactivatingPassenger, setDeactivatingPassenger] = React.useState<PassengerRecord | null>(null);
  const [deactivationReason, setDeactivationReason] = React.useState('');
  const [deactivationNotes, setDeactivationNotes] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');
  const [offset, setOffset] = React.useState(0);
  const [total, setTotal] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);
  const [activePassengerId, setActivePassengerId] = React.useState('');
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');

  React.useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    setError('');

    getPassengers(PAGE_SIZE, offset, statusFilter)
      .then((response) => {
        if (isActive) {
          const records = normalizePassengers(response);
          setPassengers(records);
          setTotal(readNestedNumber(response, ['total', 'count', 'passengers_total', 'total_passengers']) ?? records.length);
        }
      })
      .catch((requestError) => {
        if (isActive) {
          setPassengers([]);
          setError(requestError instanceof Error ? requestError.message : 'Unable to load passengers');
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [offset, statusFilter]);

  React.useEffect(() => {
    if (!selectedPassenger && !deactivatingPassenger) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedPassenger(null);
        setDeactivatingPassenger(null);
      }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [deactivatingPassenger, selectedPassenger]);

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const visiblePassengers = normalizedSearch
    ? passengers.filter((passenger) => [
      passenger.name,
      passenger.email,
      passenger.phone,
      passenger.status,
      passenger.id,
    ].some((value) => value.toLowerCase().includes(normalizedSearch)))
    : passengers;
  const pageStart = total ? offset + 1 : 0;
  const pageEnd = Math.min(offset + passengers.length, total);

  const updatePassengerStatus = async (
    passenger: PassengerRecord,
    isActive: boolean,
    details?: { reason: string; notes: string },
  ) => {
    setActivePassengerId(passenger.id);
    setError('');
    setMessage('');

    try {
      await setPassengerActive(passenger.id, isActive, details);
      const status = isActive ? 'active' : 'inactive';
      setPassengers((current) => current.map((item) => item.id === passenger.id ? { ...item, status } : item));
      setSelectedPassenger((current) => current?.id === passenger.id ? { ...current, status } : current);
      setDeactivatingPassenger(null);
      setDeactivationReason('');
      setDeactivationNotes('');
      setMessage(`${passenger.name} was ${isActive ? 'activated' : 'deactivated'}.`);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to update passenger status');
    } finally {
      setActivePassengerId('');
    }
  };

  return (
    <div className="passengers-page">
      <div className="section-head passengers-head">
        <div>
          <h2>Passengers</h2>
          <span>{total.toLocaleString()} passenger{total === 1 ? '' : 's'}</span>
        </div>
        <div className="passenger-filters">
          <label>
            <span className="sr-only">Filter passengers by status</span>
            <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value); setOffset(0); }}>
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </label>
          <span className="pill info">{visiblePassengers.length} shown</span>
        </div>
      </div>

      {error ? <div className="kyc-message passengers-error" role="alert">{error}</div> : null}
      {message ? <div className="kyc-message" role="status">{message}</div> : null}

      <div className="data-table passengers-table">
        <table>
          <thead>
            <tr>
              <th>Passenger</th>
              <th>Phone</th>
              <th>Verification</th>
              <th>Trips</th>
              <th>Joined</th>
              <th>Status</th>
              <th><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td className="kyc-table-state" colSpan={7}>Loading passengers...</td></tr>
            ) : visiblePassengers.length ? visiblePassengers.map((passenger) => (
              <tr key={passenger.id}>
                <td data-label="Passenger">
                  <div className="row-cell">
                    <span className="row-avatar">{passenger.initials}</span>
                    <span className="row-info">
                      <strong>{passenger.name}</strong>
                      <span>{passenger.email}</span>
                    </span>
                  </div>
                </td>
                <td data-label="Phone">{passenger.phone}</td>
                <td data-label="Verification">
                  <div className="passenger-verification">
                    <span className={`pill ${passenger.emailVerified ? 'success' : 'muted'}`}>email</span>
                    <span className={`pill ${passenger.phoneVerified ? 'success' : 'muted'}`}>phone</span>
                  </div>
                </td>
                <td data-label="Trips">{passenger.trips.toLocaleString()}</td>
                <td data-label="Joined">{passenger.joined}</td>
                <td data-label="Status"><span className={`pill ${passengerStatusTone(passenger.status)}`}>{passenger.status}</span></td>
                <td>
                  <div className="passenger-row-actions">
                    <button
                      aria-label={`View ${passenger.name}`}
                      className="btn-icon borderless passenger-view-btn"
                      title="View passenger"
                      type="button"
                      onClick={() => setSelectedPassenger(passenger)}
                    >
                      <EyeIcon />
                    </button>
                    {passenger.status !== 'active' ? (
                      <button className="btn btn-sm btn-outline" disabled={activePassengerId === passenger.id} type="button" onClick={() => updatePassengerStatus(passenger, true)}>
                        Activate
                      </button>
                    ) : null}
                    {passenger.status !== 'inactive' ? (
                      <button className="btn btn-sm passenger-deactivate-btn" disabled={activePassengerId === passenger.id} type="button" onClick={() => setDeactivatingPassenger(passenger)}>
                        Deactivate
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            )) : (
              <tr>
                <td className="kyc-table-state" colSpan={7}>
                  {normalizedSearch ? `No passengers match "${searchQuery.trim()}".` : 'No passengers found.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="passengers-pagination">
        <span>Showing {pageStart}-{pageEnd} of {total.toLocaleString()}</span>
        <div>
          <button className="btn" disabled={offset === 0 || isLoading} type="button" onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
            Previous
          </button>
          <button className="btn" disabled={offset + PAGE_SIZE >= total || isLoading} type="button" onClick={() => setOffset(offset + PAGE_SIZE)}>
            Next
          </button>
        </div>
      </div>

      {selectedPassenger ? (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setSelectedPassenger(null)}>
          <section
            aria-labelledby="passenger-details-title"
            aria-modal="true"
            className="signup-dialog passenger-details-dialog"
            role="dialog"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="signup-dialog-head">
              <div>
                <span className="row-avatar">{selectedPassenger.initials}</span>
                <div>
                  <h2 id="passenger-details-title">{selectedPassenger.name}</h2>
                  <span className={`pill ${passengerStatusTone(selectedPassenger.status)}`}>{selectedPassenger.status}</span>
                </div>
              </div>
              <button className="btn-icon borderless" type="button" aria-label="Close passenger details" onClick={() => setSelectedPassenger(null)}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <dl className="kyc-details">
              <PassengerDetail label="Email" value={selectedPassenger.email} />
              <PassengerDetail label="Phone" value={selectedPassenger.phone} />
              <PassengerDetail label="Email verified" value={selectedPassenger.emailVerified ? 'Yes' : 'No'} />
              <PassengerDetail label="Phone verified" value={selectedPassenger.phoneVerified ? 'Yes' : 'No'} />
              <PassengerDetail label="Total trips" value={selectedPassenger.trips.toLocaleString()} />
              <PassengerDetail label="Joined" value={selectedPassenger.joined} />
              <PassengerDetail label="Passenger ID" value={selectedPassenger.id} document />
            </dl>
            <div className="kyc-dialog-actions">
              <button className="btn" type="button" onClick={() => setSelectedPassenger(null)}>Close</button>
              {selectedPassenger.status !== 'active' ? (
                <button className="btn btn-primary" disabled={activePassengerId === selectedPassenger.id} type="button" onClick={() => updatePassengerStatus(selectedPassenger, true)}>
                  Activate passenger
                </button>
              ) : null}
              {selectedPassenger.status !== 'inactive' ? (
                <button className="btn btn-danger" disabled={activePassengerId === selectedPassenger.id} type="button" onClick={() => setDeactivatingPassenger(selectedPassenger)}>
                  Deactivate passenger
                </button>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}

      {deactivatingPassenger ? (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setDeactivatingPassenger(null)}>
          <form
            aria-labelledby="deactivate-passenger-title"
            aria-modal="true"
            className="signup-dialog passenger-deactivate-dialog"
            role="dialog"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={(event) => {
              event.preventDefault();
              updatePassengerStatus(deactivatingPassenger, false, {
                reason: deactivationReason.trim(),
                notes: deactivationNotes.trim(),
              });
            }}
          >
            <div className="signup-dialog-head">
              <div>
                <span className="row-avatar">{deactivatingPassenger.initials}</span>
                <div>
                  <h2 id="deactivate-passenger-title">Deactivate passenger</h2>
                  <span>{deactivatingPassenger.name}</span>
                </div>
              </div>
              <button className="btn-icon borderless" type="button" aria-label="Close deactivation dialog" onClick={() => setDeactivatingPassenger(null)}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="form-group">
              <label htmlFor="deactivation-reason">Reason</label>
              <input
                autoFocus
                id="deactivation-reason"
                placeholder="Reason for deactivation"
                required
                value={deactivationReason}
                onChange={(event) => setDeactivationReason(event.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="deactivation-notes">Notes</label>
              <textarea
                id="deactivation-notes"
                placeholder="Additional notes (optional)"
                value={deactivationNotes}
                onChange={(event) => setDeactivationNotes(event.target.value)}
              />
            </div>
            <div className="kyc-dialog-actions">
              <button className="btn" type="button" onClick={() => setDeactivatingPassenger(null)}>Cancel</button>
              <button
                className="btn btn-danger"
                disabled={!deactivationReason.trim() || activePassengerId === deactivatingPassenger.id}
                type="submit"
              >
                Deactivate passenger
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}

function normalizePassengers(response: unknown): PassengerRecord[] {
  return getPassengerSource(response).map((passenger, index) => {
    const user = readObject(passenger, 'user') ?? readObject(passenger, 'passenger') ?? {};
    const profile = readObject(passenger, 'profile') ?? readObject(user, 'profile') ?? {};
    const name = readString(passenger, 'full_name', 'name', 'username')
      ?? readString(user, 'full_name', 'name', 'username')
      ?? readString(profile, 'full_name', 'name')
      ?? readString(passenger, 'email')
      ?? readString(user, 'email')
      ?? 'Unknown passenger';
    return {
      id: readString(passenger, 'id', 'user_id', 'passenger_id') ?? readString(user, 'id', 'user_id') ?? String(index),
      initials: getInitials(name),
      name,
      email: readString(passenger, 'email') ?? readString(user, 'email') ?? 'No email address',
      phone: readString(passenger, 'phone_number', 'phone')
        ?? readString(user, 'phone_number', 'phone')
        ?? readString(profile, 'phone_number', 'phone')
        ?? 'No phone number',
      status: getPassengerStatus(passenger, user),
      emailVerified: readBoolean(passenger, 'is_email_verified', 'email_verified')
        ?? readBoolean(user, 'is_email_verified', 'email_verified')
        ?? false,
      phoneVerified: readBoolean(passenger, 'is_phone_verified', 'phone_verified')
        ?? readBoolean(user, 'is_phone_verified', 'phone_verified')
        ?? false,
      trips: readNumber(passenger, 'trips_total', 'total_trips', 'trip_count', 'trips_count', 'rides_total', 'total_rides') ?? 0,
      joined: formatDate(
        readString(passenger, 'created_at', 'joined_at', 'registered_at')
        ?? readString(user, 'created_at', 'joined_at', 'registered_at'),
      ),
    };
  });
}

function getPassengerSource(response: unknown): Record<string, unknown>[] {
  if (Array.isArray(response)) return response.filter(isObject);
  if (!isObject(response)) return [];

  for (const key of ['passengers', 'items', 'results', 'records', 'users', 'passenger_profiles']) {
    const value = response[key];
    if (Array.isArray(value)) return value.filter(isObject);
    if (isObject(value)) {
      const passengers = getPassengerSource(value);
      if (passengers.length) return passengers;
    }
  }

  for (const key of ['data', 'result', 'payload', 'response']) {
    const nested = response[key];
    if (isObject(nested) || Array.isArray(nested)) {
      const passengers = getPassengerSource(nested);
      if (passengers.length) return passengers;
    }
  }

  for (const value of Object.values(response)) {
    if (Array.isArray(value) && value.some(isObject)) return value.filter(isObject);
    if (isObject(value)) {
      const passengers = getPassengerSource(value);
      if (passengers.length) return passengers;
    }
  }

  return [];
}

function PassengerDetail({ document = false, label, value }: { document?: boolean; label: string; value: string }) {
  return <div><dt>{label}</dt><dd className={document ? 'kyc-document' : undefined}>{value}</dd></div>;
}

function EyeIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function formatDate(value: string | undefined) {
  if (!value) return 'Unknown';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function getInitials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'NA';
}

function readString(source: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value) return value;
  }
  return undefined;
}

function readNumber(source: unknown, ...keys: string[]) {
  if (!isObject(source)) return undefined;
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number') return value;
  }
  return undefined;
}

function readBoolean(source: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'boolean') return value;
  }
  return undefined;
}

function readObject(source: Record<string, unknown>, key: string) {
  const value = source[key];
  return isObject(value) ? value : undefined;
}

function readNestedNumber(response: unknown, keys: string[]): number | undefined {
  if (!isObject(response)) return undefined;

  const direct = readNumber(response, ...keys);
  if (direct !== undefined) return direct;

  for (const nested of Object.values(response)) {
    if (!isObject(nested)) continue;
    const value = readNestedNumber(nested, keys);
    if (value !== undefined) return value;
  }

  return undefined;
}

function getPassengerStatus(passenger: Record<string, unknown>, user: Record<string, unknown>) {
  const status = readString(passenger, 'status', 'account_status') ?? readString(user, 'status', 'account_status');
  if (status) return status.toLowerCase();

  const isActive = readBoolean(passenger, 'is_active', 'active') ?? readBoolean(user, 'is_active', 'active');
  if (isActive === true) return 'active';
  if (isActive === false) return 'inactive';
  return 'unknown';
}

function passengerStatusTone(status: string) {
  if (status === 'active') return 'success';
  if (status === 'inactive') return 'danger';
  return 'muted';
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
